import { useEffect } from "react";
import { Flex, Box, Text } from "rebass/styled-components";
import { ToastProvider } from "react-toast-notifications";

import useTranslation from "next-translate/useTranslation";

import {
  Dog,
  useGetCurrentUserForRegistrationQuery,
  GetCurrentUserForRegistrationDocument
} from "../../../generated/graphql";
import { useStepByStepDispatch } from "../../identity/StepByStepContext";
import { useState } from "react";
import Heading from "../../common/Heading";
import DogCard from "../../common/DogCard";
import Icon from "../../common/Icon";
import { WarningToast } from "../../common/toasts";

import AddDog from "./AddDog";
import AddCareInfo from "./AddCareInfo";

export type DogWithOmitForRegistration = Omit<Dog, "user" | "avatar">;

interface Props {
  save: boolean;
  reportWhenFormOpen: (x: boolean) => void;
}

const UserDogs = ({ save, reportWhenFormOpen }: Props) => {
  const dispatch = useStepByStepDispatch();

  const { t } = useTranslation("user/registration");
  const { data } = useGetCurrentUserForRegistrationQuery();

  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [addedDog, setAddedDog] = useState<DogWithOmitForRegistration>();

  const moveFwd = () => {
    dispatch({
      type: "changeActive",
      payload: { active: 1 }
    });
  };

  const openingForm = () => {
    setFormOpen(true);
    reportWhenFormOpen(true);
  };

  const closingForm = () => {
    setFormOpen(false);
    reportWhenFormOpen(false);
  };

  //scroll back to top and sets the newly added dog
  const onDogAdded = (dog: DogWithOmitForRegistration) => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    setAddedDog(dog);
  };

  //determines whether the form should be opened by default
  useEffect(() => {
    if (!data) return;

    if (!data?.currentUser?.dogs?.length) {
      openingForm();
    } else {
      closingForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <ToastProvider components={{ Toast: WarningToast }}>
      <Heading as="h3" fontWeight="bold">
        {t("myDogs")}
      </Heading>
      <Text as="p" mt={2}>
        {t("myDogsDescription")}
      </Text>
      {!!data?.currentUser?.dogs?.length && !addedDog && (
        <>
          <Box mb={3}>
            {data?.currentUser?.dogs?.map((dog, key) => (
              <DogCard dog={dog} key={key} editable />
            ))}
          </Box>
          <Flex
            alignItems="center"
            flexDirection="row-reverse"
            onClick={formOpen ? closingForm : openingForm}
          >
            <Text ml={2}>{t("addDog")}</Text>
            <Box variant="plusButton">
              <Icon name="plus" color="veryDarkGray" />
            </Box>
          </Flex>
        </>
      )}

      {formOpen && (
        <Box sx={{ borderTop: "default", marginTop: 2, paddingTop: 1 }}>
          {!addedDog ? (
            <AddDog triggerSubmit={save} onDogAdded={onDogAdded} />
          ) : (
            <AddCareInfo
              dog={addedDog}
              triggerSubmit={save}
              onCareInfoAdded={moveFwd}
              refetchQuery={GetCurrentUserForRegistrationDocument}
            />
          )}
        </Box>
      )}
    </ToastProvider>
  );
};

export default UserDogs;
